import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { passwordClipboardProps } from "../../utils/passwordClipboard";
import {
  Lock,
  ShieldCheck,
  Clock,
  Laptop,
  Monitor,
  LogOut,
  Trash2,
  ChevronDown,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowRight,
  Mail,
  X,
  CircleAlert,
} from "lucide-react";

import NotificationToggle from "../../components/NotificationToggle";
import {
  changePassword,
  fetchTwoFAStatus,
  startTwoFASetup,
  resendTwoFACode,
  verifyTwoFA,
  disableTwoFA,
  requestDisableTwoFACode,
  fetchLoginActivity,
  fetchActiveDevices,
  logoutDevice,
  logoutAllDevices,
} from "../../service/userService";
import "../../assets/css/AccountSecurity.css";

const getPasswordStrength = (password) => {
  const checks = [password.length >= 8, /[A-Z]/.test(password), /[a-z]/.test(password), /[0-9!@#$%^&*(),.?":{}|<>]/.test(password)];
  const score = checks.filter(Boolean).length;
  const meta = [
    { label: "", color: "#EAEAEA" },
    { label: "Weak", color: "#C72430" },
    { label: "Medium", color: "#E5942F" },
    { label: "Strong", color: "#2E9E5B" },
    { label: "Very Strong", color: "#20895F" },
  ];
  return { score, ...meta[score] };
};

const getDeviceLabel = (device) =>
  device?.device_name ||
  device?.device ||
  [device?.browser, device?.os || device?.platform].filter(Boolean).join(" • ") ||
  `Device #${device?.id ?? "?"}`;

const getDeviceIp = (device) => device?.ip_address || device?.ip || "";

const getDeviceLocation = (device) =>
  device?.location || [device?.city, device?.country].filter(Boolean).join(", ") || getDeviceIp(device) || "";

const isCurrentDevice = (device) => {
  const flag = device?.is_current ?? device?.current ?? device?.is_current_device;
  return flag === true || flag === 1 || flag === "1";
};

const stripBrowserVersion = (browser) => (browser ? browser.replace(/\s+[\d.]+$/, "").trim() : browser);

const getLoginDeviceLabel = (entry) =>
  [stripBrowserVersion(entry?.browser), entry?.operating_system || entry?.os || entry?.platform].filter(Boolean).join(" on ") ||
  entry?.device_name ||
  entry?.device ||
  "Unknown device";

const getLoginLocation = (entry) =>
  entry?.location || [entry?.city, entry?.country].filter(Boolean).join(", ") || entry?.ip_address || entry?.ip || "";

const getLoginTime = (entry) => entry?.login_at || entry?.login_time || entry?.date || entry?.created_at || entry?.timestamp || "";

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));

const getStoredEmail = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.personal_email || user.email || user.useremail || "your registered email";
  } catch {
    return "your registered email";
  }
};

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email;
  const [name, domain] = email.split("@");
  if (name.length <= 2) return `${name[0] || ""}***@${domain}`;
  return `${name.slice(0, 2)}${"*".repeat(Math.min(5, name.length - 2))}@${domain}`;
};

const getTwoFASetupDetails = (response) => {
  const data = response?.data || response || {};
  const qrValue = data.qr_code || data.qrCode || data.qr_code_url || data.qr_url || data.qr_image || "";
  let qrCode = qrValue;
  if (qrValue && !/^(data:|https?:|blob:)/i.test(qrValue)) {
    qrCode = qrValue.trim().startsWith("<svg")
      ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrValue)}`
      : `data:image/png;base64,${qrValue}`;
  }
  return {
    qrCode,
    secret: data.secret || data.setup_key || data.manual_entry_key || data.shared_secret || "",
  };
};

const PasswordField = ({ label, value, onChange, show, onToggleShow }) => (
  <div className="as-field mb-3">
    <label className="as-field-label">{label}</label>
    <div className="as-input-wrap">
      <input
        type={show ? "text" : "password"}
        className="as-input"
        value={value}
        onChange={onChange}
        {...passwordClipboardProps}
        autoComplete={label === "Current Password" ? "current-password" : "new-password"}
      />
      <button type="button" className="as-input-eye" onClick={onToggleShow} aria-label={show ? "Hide password" : "Show password"}>
        {show ? <Eye size={16} /> : <EyeOff size={16} />}
      </button>
    </div>
  </div>
);

const AccountSecurity = () => {
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [twoFASetupOpen, setTwoFASetupOpen] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState("method");
  const [twoFAMethod, setTwoFAMethod] = useState("email");
  const [configuredTwoFAMethod, setConfiguredTwoFAMethod] = useState(null);
  const [disablingTwoFA, setDisablingTwoFA] = useState(false);
  const [twoFAError, setTwoFAError] = useState("");
  const [twoFACode, setTwoFACode] = useState(Array(6).fill(""));
  const [twoFASubmitting, setTwoFASubmitting] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [authenticatorSetup, setAuthenticatorSetup] = useState({ qrCode: "", secret: "" });
  const [showAuthenticatorKey, setShowAuthenticatorKey] = useState(false);
  const otpRefs = useRef([]);
  const accountEmail = useMemo(getStoredEmail, []);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [loginActivityOpen, setLoginActivityOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [savingPassword, setSavingPassword] = useState(false);

  const [showFullLoginHistory, setShowFullLoginHistory] = useState(false);

  const [loginActivity, setLoginActivity] = useState([]);
  const [loginActivityLoading, setLoginActivityLoading] = useState(false);
  const [loginActivityLoaded, setLoginActivityLoaded] = useState(false);

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  useEffect(() => {
    let cancelled = false;

    fetchTwoFAStatus()
      .then((res) => {
        if (cancelled) return;
        setIs2FAEnabled(Boolean(res?.data?.is_enabled));
        const method = res?.data?.auth_method;
        if (["email", "authenticator"].includes(method)) setConfiguredTwoFAMethod(method);
      })
      .catch(() => {
        // Leave 2FA off if the status can't be loaded.
      })
      .finally(() => {
        if (!cancelled) setTwoFALoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!resendSeconds) return undefined;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (!loginActivityOpen || loginActivityLoaded) return;

    let cancelled = false;
    setLoginActivityLoading(true);

    fetchLoginActivity()
      .then((res) => {
        if (cancelled) return;
        setLoginActivity(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        // Leave the list empty if it can't be loaded.
      })
      .finally(() => {
        if (cancelled) return;
        setLoginActivityLoading(false);
        setLoginActivityLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [loginActivityOpen, loginActivityLoaded]);

  useEffect(() => {
    if (!devicesOpen || devicesLoaded) return;

    let cancelled = false;
    setDevicesLoading(true);

    fetchActiveDevices()
      .then((res) => {
        if (cancelled) return;
        setDevices(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        // Leave the list empty if it can't be loaded.
      })
      .finally(() => {
        if (cancelled) return;
        setDevicesLoading(false);
        setDevicesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [devicesOpen, devicesLoaded]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.warn("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password must match.");
      return;
    }

    if (strength.score < 4) {
      toast.warn("Your new password must meet all the requirements listed below.");
      return;
    }

    setSavingPassword(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      await Swal.fire({
        title: "Password updated",
        text: "Your password has been changed successfully.",
        icon: "success",
        confirmButtonColor: "#c72430",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't update password. Please try again in a moment.");
    } finally {
      setSavingPassword(false);
    }
  };

  const closeTwoFASetup = () => {
    if (twoFASubmitting) return;
    setTwoFASetupOpen(false);
    setDisablingTwoFA(false);
    setTwoFAError("");
    setTwoFAStep("method");
    setTwoFACode(Array(6).fill(""));
    setResendSeconds(0);
    setAuthenticatorSetup({ qrCode: "", secret: "" });
    setShowAuthenticatorKey(false);
  };

  const openDisableVerification = async () => {
    if (twoFASubmitting) return;
    setTwoFASubmitting(true);
    setTwoFAError("");
    try {
      let method = configuredTwoFAMethod || "email";
      if (method === "email") {
        try {
          await requestDisableTwoFACode();
        } catch (err) {
          // The status API may omit the configured method. Only this documented
          // response allows switching to the authenticator-code prompt.
          const message = err?.response?.data?.message || err.message;
          if (message === "OTP request is only required for email two-factor authentication") {
            method = "authenticator";
          } else {
            throw err;
          }
        }
      }
      setTwoFACode(Array(6).fill(""));
      setResendSeconds(method === "email" ? 45 : 0);
      setTwoFAMethod(method);
      setTwoFAStep("verify");
      window.setTimeout(() => otpRefs.current[0]?.focus(), 0);
    } catch (err) {
      setTwoFAError(err?.response?.data?.message || err.message || "Unable to send a verification code. Please try again.");
    } finally {
      setTwoFASubmitting(false);
    }
  };

  const handleStartTwoFA = async () => {
    if (twoFASubmitting) return;
    setTwoFASubmitting(true);
    setTwoFAError("");
    try {
      if (disablingTwoFA) {
        if (twoFAMethod === "email") await requestDisableTwoFACode();
        setTwoFAStep("verify");
        setResendSeconds(twoFAMethod === "email" ? 45 : 0);
        window.setTimeout(() => otpRefs.current[0]?.focus(), 0);
        return;
      }
      const response = await startTwoFASetup({ auth_method: twoFAMethod });
      if (twoFAMethod === "authenticator") {
        const setup = getTwoFASetupDetails(response);
        setAuthenticatorSetup(setup);
        setShowAuthenticatorKey(!setup.qrCode);
      }
      setTwoFAStep("verify");
      setResendSeconds(twoFAMethod === "email" ? 45 : 0);
      window.setTimeout(() => otpRefs.current[0]?.focus(), 0);
    } catch (err) {
      if (disablingTwoFA) {
        setTwoFAError(err?.response?.data?.message || err.message || "Unable to send verification code.");
        return;
      }
      toast.error(err?.response?.data?.message || "Couldn't start 2FA setup. Please try again in a moment.");
    } finally {
      setTwoFASubmitting(false);
    }
  };

  const handleCodeChange = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, "").slice(-1);
    if (twoFAError) setTwoFAError("");
    setTwoFACode((current) => current.map((value, i) => (i === index ? digit : value)));
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index, event) => {
    if (event.key === "Backspace" && !twoFACode[index] && index > 0) otpRefs.current[index - 1]?.focus();
    if (event.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (event.key === "ArrowRight" && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleCodePaste = (event) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    event.preventDefault();
    if (twoFAError) setTwoFAError("");
    setTwoFACode(Array.from({ length: 6 }, (_, index) => digits[index] || ""));
    otpRefs.current[Math.min(digits.length, 6) - 1]?.focus();
  };

  const handleVerifyTwoFA = async () => {
    const otp = twoFACode.join("");
    if (!/^\d{6}$/.test(otp) || twoFASubmitting) return;
    setTwoFASubmitting(true);
    setTwoFAError("");
    try {
      if (disablingTwoFA) {
        await disableTwoFA({ otp });
        setIs2FAEnabled(false);
        setConfiguredTwoFAMethod(null);
        setTwoFACode(Array(6).fill(""));
        setResendSeconds(0);
        setTwoFAStep("disable-success");
        return;
      }
      await verifyTwoFA({ otp, auth_method: twoFAMethod });
      setConfiguredTwoFAMethod(twoFAMethod);
      setIs2FAEnabled(true);
      setTwoFASetupOpen(false);
      setTwoFAStep("method");
      setTwoFACode(Array(6).fill(""));
      setResendSeconds(0);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: is2FAEnabled
          ? "2FA verification method updated."
          : `2FA successfully enabled via ${twoFAMethod === "email" ? "Email" : "Google Authenticator"}.`,
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
        customClass: {
          container: "as-2fa-toast-container",
          popup: "as-2fa-toast",
          title: "as-2fa-toast-title",
        },
      });
    } catch (err) {
      if (disablingTwoFA) {
        setTwoFAError(err?.response?.data?.message || err.message || "Verification failed. Please try again.");
        return;
      }
      toast.error(err?.response?.data?.message || "The code is incorrect or has expired. Please try again.");
    } finally {
      setTwoFASubmitting(false);
    }
  };

  const handleResendTwoFA = async () => {
    if (resendSeconds || twoFASubmitting) return;
    setTwoFASubmitting(true);
    setTwoFAError("");
    try {
      if (disablingTwoFA) await requestDisableTwoFACode();
      else await resendTwoFACode({ auth_method: twoFAMethod });
      setTwoFACode(Array(6).fill(""));
      setResendSeconds(45);
      otpRefs.current[0]?.focus();
    } catch (err) {
      if (disablingTwoFA) {
        setTwoFAError(err?.response?.data?.message || err.message || "Unable to resend verification code.");
        return;
      }
      toast.error(err?.response?.data?.message || "Couldn't resend code. Please try again in a moment.");
    } finally {
      setTwoFASubmitting(false);
    }
  };

  const handleTryAnotherDisableMethod = async () => {
    if (twoFASubmitting) return;
    const nextMethod = twoFAMethod === "email" ? "authenticator" : "email";
    setTwoFASubmitting(true);
    setTwoFAError("");
    try {
      if (nextMethod === "email") await requestDisableTwoFACode();
      setTwoFAMethod(nextMethod);
      setTwoFACode(Array(6).fill(""));
      setResendSeconds(nextMethod === "email" ? 45 : 0);
      window.setTimeout(() => otpRefs.current[0]?.focus(), 0);
    } catch (err) {
      setTwoFAError(err?.response?.data?.message || err.message || "Unable to use that verification method.");
    } finally {
      setTwoFASubmitting(false);
    }
  };

  const handleManage2FA = () => {
    if (twoFALoading || twoFASubmitting) return;
    setDisablingTwoFA(false);
    setTwoFAError("");
    setTwoFAStep("method");
    setTwoFACode(Array(6).fill(""));
    setResendSeconds(0);
    setAuthenticatorSetup({ qrCode: "", secret: "" });
    setShowAuthenticatorKey(false);
    setTwoFASetupOpen(true);
  };

  const handleDisable2FA = () => {
    if (twoFALoading || twoFASubmitting) return;
    setDisablingTwoFA(true);
    setTwoFAError("");
    setTwoFACode(Array(6).fill(""));
    setResendSeconds(0);
    setTwoFAStep("disable-confirm");
    setTwoFASetupOpen(true);
  };

  const handleSignOutDevice = async (device) => {
    const label = getDeviceLabel(device);
    const location = getDeviceLocation(device);

    const result = await Swal.fire({
      html: `
        <div class="as-swal-icon-wrap">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="20" height="14" x="2" y="3" rx="2"></rect>
            <line x1="8" x2="16" y1="21" y2="21"></line>
            <line x1="12" x2="12" y1="17" y2="21"></line>
          </svg>
        </div>
        <h3 class="as-swal-title">Sign out this device?</h3>
        <p class="as-swal-text">You'll be signed out of <strong>${escapeHtml(label)}</strong>${
        location ? ` in ${escapeHtml(location)}` : ""
      } immediately.</p>
      `,
      showCancelButton: true,
      confirmButtonText: "Sign out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      buttonsStyling: false,
      customClass: {
        popup: "as-swal-popup",
        actions: "as-swal-actions",
        confirmButton: "as-swal-confirm-btn",
        cancelButton: "as-swal-cancel-btn",
      },
    });

    if (!result.isConfirmed) return;

    try {
      await logoutDevice(device.id);

      setDevices((prev) => prev.filter((d) => d.id !== device.id));

      Swal.fire({
        title: "Signed out",
        text: `${label} has been signed out.`,
        icon: "success",
        confirmButtonColor: "#c72430",
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't sign out device. Please try again in a moment.");
    }
  };

  const handleSignOutAllDevices = async () => {
    const result = await Swal.fire({
      title: "Sign out of all devices?",
      text: "You'll be signed out everywhere except this device.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c72430",
      cancelButtonColor: "#858C95",
      confirmButtonText: "Yes, sign out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await logoutAllDevices();
      setDevicesLoaded(false);

      Swal.fire({
        title: "Signed out",
        text: "All other devices have been signed out.",
        icon: "success",
        confirmButtonColor: "#c72430",
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't sign out other devices. Please try again in a moment.");
    }
  };

  const handleDeleteAccount = async () => {
    const result = await Swal.fire({
      title: "Delete your account?",
      text: "This action cannot be undone. All your data, surveys, coins, and history will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c72430",
      cancelButtonColor: "#858C95",
      confirmButtonText: "Yes, delete my account",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Account deletion requested",
      text: "We've received your request to delete this account.",
      icon: "success",
      confirmButtonColor: "#c72430",
    });
  };

  return (
    <div className="dashboard-search-results account-security">
      <div className="row g-3">
        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            <h1 className="sr-page-title">Account &amp; Security</h1>
            <p className="sr-page-subtitle">Manage your account settings and keep your data safe.</p>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Change Password */}
            <div className="as-row">
              <button
                type="button"
                className="as-row-toggle d-flex align-items-center justify-content-between w-100"
                aria-expanded={passwordOpen}
                onClick={() => setPasswordOpen((open) => !open)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="as-icon-chip">
                    <Lock size={18} />
                  </div>
                  <div className="text-start">
                    <div className="as-row-title">Change Password</div>
                    <div className="as-row-subtitle">Update your password regularly to keep your account secure.</div>
                  </div>
                </div>
                <ChevronDown size={20} className={`as-chevron-icon ${passwordOpen ? "open" : ""}`} />
              </button>

              {passwordOpen && (
                <form className="as-collapsible-content" onSubmit={handleUpdatePassword}>
                  <PasswordField
                    label="Current Password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    show={showCurrent}
                    onToggleShow={() => setShowCurrent((v) => !v)}
                  />

                  <PasswordField
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    show={showNew}
                    onToggleShow={() => setShowNew((v) => !v)}
                  />

                  <div className="as-strength-meter mb-3">
                    <div className="as-strength-bars">
                      {[0, 1, 2, 3].map((i) => (
                        <span key={i} className="as-strength-bar" style={{ background: i < strength.score ? strength.color : "#EAEAEA" }} />
                      ))}
                    </div>
                    {newPassword && (
                      <div className="as-strength-label">
                        Password strength: <span style={{ color: strength.color }}>{strength.label}</span>
                      </div>
                    )}
                  </div>

                  <PasswordField
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    show={showConfirm}
                    onToggleShow={() => setShowConfirm((v) => !v)}
                  />

                  <div className="as-requirements-box d-flex align-items-start gap-2">
                    <Lock size={14} className="mt-1" />
                    <div>
                      <div className="as-requirements-title">Password must contain:</div>
                      <ul className="as-requirements-list">
                        <li>At least 8 characters</li>
                        <li>One uppercase letter</li>
                        <li>One lowercase letter</li>
                        <li>One number or special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="as-outline-btn" onClick={() => setPasswordOpen(false)} disabled={savingPassword}>
                      Cancel
                    </button>
                    <button type="submit" className="as-danger-btn" disabled={savingPassword}>
                      {savingPassword ? "Updating..." : "Update Password"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Two-Factor Authentication */}
            <div className="as-row">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="as-icon-chip">
                    <ShieldCheck size={18} />
                  </div>
                  <div>
                    <div className="as-row-title">Two-Factor Authentication (2FA)</div>
                    <div className="as-row-subtitle">Add an extra layer of security to your account.</div>
                  </div>
                </div>
                <NotificationToggle
                  label=""
                  variant="red"
                  fullWidth={false}
                  checked={is2FAEnabled}
                  onChange={(value) => (value ? handleManage2FA() : handleDisable2FA())}
                  disabled={twoFALoading || twoFASubmitting}
                />
              </div>

              {is2FAEnabled && (
                <div className="as-2fa-panel d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="as-2fa-icon">
                      <Check size={13} strokeWidth={3} />
                    </div>
                    <div>
                      <div className="as-2fa-title">2FA is enabled</div>
                      <div className="as-2fa-subtitle">Authentication code is required at login.</div>
                    </div>
                  </div>
                  <button type="button" className="as-manage-btn" onClick={handleManage2FA}>
                    Manage 2FA
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Login Activity */}
            <div className="as-row">
              <button
                type="button"
                className="as-row-toggle d-flex align-items-center justify-content-between w-100"
                aria-expanded={loginActivityOpen}
                onClick={() => setLoginActivityOpen((open) => !open)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="as-icon-chip">
                    <Clock size={18} />
                  </div>
                  <div className="text-start">
                    <div className="as-row-title">Login Activity</div>
                    <div className="as-row-subtitle">See your recent account activity and login history.</div>
                  </div>
                </div>
                <ChevronDown size={20} className={`as-chevron-icon ${loginActivityOpen ? "open" : ""}`} />
              </button>

              {loginActivityOpen && (
                <div className="as-collapsible-content">
                  {loginActivityLoading ? (
                    <div className="as-row-subtitle text-center py-3">Loading login activity...</div>
                  ) : loginActivity.length === 0 ? (
                    <div className="as-row-subtitle text-center py-3">No login activity found.</div>
                  ) : (
                    <>
                      <div className="as-login-card d-flex align-items-center justify-content-between">
                        <div>
                          <div className="as-login-label">Last login</div>
                          <div className="as-login-value">{getLoginTime(loginActivity[0])}</div>
                        </div>
                        <div className="text-end">
                          <div className="as-login-value">{getLoginLocation(loginActivity[0])}</div>
                          <div className="as-login-label">{getLoginDeviceLabel(loginActivity[0])}</div>
                        </div>
                      </div>

                      {loginActivity.length > 1 && (
                        <>
                          {showFullLoginHistory && (
                            <div className="as-login-history mt-3">
                              {loginActivity.slice(1).map((entry, index) => (
                                <div key={entry.id ?? index} className="as-login-history-row d-flex align-items-center justify-content-between">
                                  <div className="as-login-label">{getLoginTime(entry)}</div>
                                  <div className="text-end">
                                    <div className="as-row-subtitle mb-0">{getLoginLocation(entry)}</div>
                                    <div className="as-row-subtitle mb-0">{getLoginDeviceLabel(entry)}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="text-center mt-3">
                            <button type="button" className="as-link-btn" onClick={() => setShowFullLoginHistory((v) => !v)}>
                              {showFullLoginHistory ? "Hide login activity" : "View all login activity"}
                              {showFullLoginHistory ? <ChevronDown size={14} className="as-chevron-icon open" /> : <ArrowRight size={14} />}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Active Devices */}
            <div className="as-row">
              <button
                type="button"
                className="as-row-toggle d-flex align-items-center justify-content-between w-100"
                aria-expanded={devicesOpen}
                onClick={() => setDevicesOpen((open) => !open)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="as-icon-chip">
                    <Laptop size={18} />
                  </div>
                  <div className="text-start">
                    <div className="as-row-title">Active Devices</div>
                    <div className="as-row-subtitle">Manage devices that are signed in to your account.</div>
                  </div>
                </div>
                <ChevronDown size={20} className={`as-chevron-icon ${devicesOpen ? "open" : ""}`} />
              </button>

              {devicesOpen && (
                <div className="as-collapsible-content">
                  {devicesLoading ? (
                    <div className="as-row-subtitle text-center py-3">Loading devices...</div>
                  ) : devices.length === 0 ? (
                    <div className="as-row-subtitle text-center py-3">No active devices found.</div>
                  ) : (
                    devices.map((device, index) => {
                      const current = isCurrentDevice(device);
                      const content = (
                        <>
                          <div className="as-device-content d-flex align-items-start gap-3 flex-grow-1">
                            <div className="as-device-icon"><Monitor size={16} /></div>
                            <div className="as-device-details text-start flex-grow-1">
                              <div className="as-device-detail">
                                <span>Browser</span>
                                <strong>{device.browser || "Unknown"}</strong>
                              </div>
                              <div className="as-device-detail">
                                <span>Device</span>
                                <strong>{device.device || device.device_name || "Unknown"}</strong>
                              </div>
                              <div className="as-device-detail">
                                <span>IP address</span>
                                <strong>{getDeviceIp(device) || "Unknown"}</strong>
                              </div>
                              <div className="as-device-detail">
                                <span>Operating system</span>
                                <strong>{device.operating_system || device.os || device.platform || "Unknown"}</strong>
                              </div>
                            </div>
                          </div>
                          {current && <span className="active-badge ft-12 fw-bold">This device</span>}
                        </>
                      );

                      return current ? (
                        <div key={device.id ?? index} className="as-device-row d-flex align-items-center justify-content-between">
                          {content}
                        </div>
                      ) : (
                        <button
                          key={device.id ?? index}
                          type="button"
                          className="as-row-toggle as-device-row d-flex align-items-center justify-content-between w-100"
                          onClick={() => handleSignOutDevice(device)}
                        >
                          {content}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Sign Out of All Devices */}
            <div className="as-row d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="as-icon-chip">
                  <LogOut size={18} />
                </div>
                <div>
                  <div className="as-row-title">Sign Out of All Devices</div>
                  <div className="as-row-subtitle">Sign out from all devices except this one.</div>
                </div>
              </div>
              <button type="button" className="as-outline-btn" onClick={handleSignOutAllDevices}>
                Sign Out All
              </button>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="sr-shell-card p-3 p-md-4">
            {/* Delete Account */}
            <div className="as-row d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="as-icon-chip as-danger">
                  <Trash2 size={18} />
                </div>
                <div>
                  <div className="as-row-title">Delete Account</div>
                  <div className="as-row-subtitle">Permanently delete your account and all your data.</div>
                </div>
              </div>
              <button type="button" className="as-danger-btn" onClick={handleDeleteAccount}>
                Delete Account
              </button>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="as-warning-banner d-flex align-items-center gap-2">
            <AlertTriangle size={18} />
            <div>
              <div className="as-warning-title">This action cannot be undone.</div>
              <div>All your data, surveys, coins, and history will be permanently deleted.</div>
            </div>
          </div>
        </div>
      </div>

      {twoFASetupOpen && (
        <div className="as-2fa-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeTwoFASetup()}>
          <section className={`as-2fa-modal ${disablingTwoFA ? "as-disable-flow" : ""}`} role="dialog" aria-modal="true" aria-labelledby="two-fa-dialog-title">
            <button type="button" className="as-2fa-close" onClick={closeTwoFASetup} aria-label="Close 2FA setup">
              <X size={28} />
            </button>

            {disablingTwoFA && twoFAStep === "disable-confirm" ? (
              <div className="as-disable-step">
                <div className="as-disable-icon as-disable-icon-danger"><AlertTriangle /></div>
                <h2 id="two-fa-dialog-title">Disable Two-Factor Authentication</h2>
                <p className="as-disable-copy">Turning off 2FA will remove the additional security layer from your account. You will only need your email and password to sign in.</p>
                <div className="as-disable-warning"><CircleAlert size={20} /><span>Your account will be less secure without 2FA.</span></div>
                {twoFAError && <p className="as-disable-request-error" role="alert">{twoFAError}</p>}
                <div className="as-disable-buttons">
                  <button type="button" className="as-disable-keep" disabled={twoFASubmitting} onClick={closeTwoFASetup}>Keep 2FA Enabled</button>
                  <button type="button" className="as-disable-confirm" disabled={twoFASubmitting} onClick={openDisableVerification}>{twoFASubmitting ? "Please wait..." : "Disable 2FA"}</button>
                </div>
              </div>
            ) : disablingTwoFA && twoFAStep === "disable-success" ? (
              <div className="as-disable-step">
                <div className="as-disable-icon as-disable-icon-success"><Check /></div>
                <h2 id="two-fa-dialog-title">Two-Factor Authentication<br />Disabled</h2>
                <p className="as-disable-copy">2FA has been disabled for your account.<br />You will now sign in with your email and password only.</p>
                <button type="button" className="as-disable-done" onClick={closeTwoFASetup}>Done</button>
              </div>
            ) : twoFAStep === "method" ? (
              <>
                <h2 id="two-fa-dialog-title">{disablingTwoFA ? "Verify to disable 2FA" : is2FAEnabled ? "Change Verification Method" : "Set up Two-Factor Authentication"}</h2>
                <p className="as-2fa-modal-intro">{disablingTwoFA ? "Select your current verification method to confirm it's you." : is2FAEnabled ? "Choose how you would like to verify your identity." : "Choose a verification method to get started."}</p>

                <button type="button" className={`as-2fa-method ${twoFAMethod === "email" ? "selected" : ""}`} onClick={() => setTwoFAMethod("email")}>
                  <Mail size={25} />
                  <span><strong>Email OTP</strong><small>Receive a 6-digit code on your registered email address.</small></span>
                  <i aria-hidden="true" />
                </button>

                <button type="button" className={`as-2fa-method ${twoFAMethod === "authenticator" ? "selected" : ""}`} onClick={() => setTwoFAMethod("authenticator")}>
                  <span className="as-google-icon" aria-hidden="true">G</span>
                  <span><strong>Google Authenticator <em>Recommended</em></strong><small>Use an authenticator app to generate secure login codes.</small></span>
                  <i aria-hidden="true" />
                </button>

                <div className="as-2fa-actions as-2fa-method-actions">
                  <button type="button" className="as-2fa-back" disabled={twoFASubmitting} onClick={closeTwoFASetup}>Cancel</button>
                  <button type="button" className="as-2fa-primary" disabled={twoFASubmitting} onClick={handleStartTwoFA}>
                    {twoFASubmitting ? "Please wait..." : "Continue"}
                  </button>
                </div>
              </>
            ) : (
              <div className={disablingTwoFA ? "as-disable-step as-disable-verify-step" : ""}>
                {disablingTwoFA && (
                  <div className={`as-disable-icon ${twoFAError ? "as-disable-icon-danger" : "as-disable-icon-lock"}`}>
                    {twoFAError ? <CircleAlert /> : <Lock />}
                  </div>
                )}
                <h2 id="two-fa-dialog-title">{disablingTwoFA ? (twoFAError ? "Incorrect code" : "Verify Your Identity") : twoFAMethod === "email" ? "Verify Your Email" : "Verify Authenticator Code"}</h2>
                {twoFAMethod === "email" ? (
                  <p className={disablingTwoFA ? "as-disable-copy" : "as-2fa-verify-copy"}>{disablingTwoFA ? (twoFAError ? "The code you entered is incorrect. Please try again." : <>Enter the 6-digit code sent to<br /><strong>{maskEmail(accountEmail)}</strong> to disable 2FA.</>) : <>We've sent a 6-digit verification code to<br /><strong>{maskEmail(accountEmail)}</strong></>}</p>
                ) : (
                  <>
                    <p className={disablingTwoFA ? "as-disable-copy" : "as-2fa-auth-copy"}>{disablingTwoFA ? (twoFAError ? "The code you entered is incorrect. Please try again." : "Enter the 6-digit code from your authenticator app to disable 2FA.") : "Download and install the Google Authenticator app from the Play Store, open it, scan this QR code, and enter the 6-digit verification code below."}</p>
                    {!disablingTwoFA && <>
                    {authenticatorSetup.qrCode && !showAuthenticatorKey && (
                      <img className="as-2fa-qr" src={authenticatorSetup.qrCode} alt="Google Authenticator setup QR code" />
                    )}
                    {showAuthenticatorKey && (
                      <div className="as-2fa-secret">
                        <span>Setup key</span>
                        <strong>{authenticatorSetup.secret || "Setup key unavailable"}</strong>
                      </div>
                    )}
                    <button
                      type="button"
                      className="as-2fa-manual"
                      onClick={() => setShowAuthenticatorKey((value) => !value)}
                      aria-expanded={showAuthenticatorKey}
                    >
                      {showAuthenticatorKey && authenticatorSetup.qrCode ? "Show QR code" : "Please enter your setup key below to proceed"}
                    </button>
                    </>}
                  </>
                )}

                <div className={`as-otp-fields ${twoFAMethod === "authenticator" ? "as-otp-fields-authenticator" : ""} ${disablingTwoFA ? "as-disable-otp" : ""} ${twoFAError ? "has-error" : ""}`} onPaste={handleCodePaste}>
                  {twoFACode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(element) => { otpRefs.current[index] = element; }}
                      value={digit}
                      onChange={(event) => handleCodeChange(index, event.target.value)}
                      onKeyDown={(event) => handleCodeKeyDown(index, event)}
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      aria-label={`Verification code digit ${index + 1}`}
                      maxLength={1}
                    />
                  ))}
                </div>

                {twoFAMethod === "email" && (
                  <p className="as-2fa-resend">Didn't receive the code? <button type="button" disabled={Boolean(resendSeconds) || twoFASubmitting} onClick={handleResendTwoFA}>Resend{resendSeconds ? ` (00:${String(resendSeconds).padStart(2, "0")})` : ""}</button></p>
                )}

                <div className={`as-2fa-actions ${disablingTwoFA ? "as-disable-verify-actions" : ""}`}>
                  {!disablingTwoFA && <button type="button" className="as-2fa-back" disabled={twoFASubmitting} onClick={() => {
                    if (disablingTwoFA) { closeTwoFASetup(); return; }
                    setTwoFAStep("method"); setTwoFACode(Array(6).fill("")); setAuthenticatorSetup({ qrCode: "", secret: "" });
                  }}>Back</button>}
                  <button type="button" className="as-2fa-verify" disabled={twoFACode.join("").length !== 6 || twoFASubmitting} onClick={handleVerifyTwoFA}>{twoFASubmitting ? "Verifying..." : disablingTwoFA ? "Verify & Disable 2FA" : "Verify"}</button>
                </div>
                {disablingTwoFA && <button type="button" className="as-disable-another" disabled={twoFASubmitting} onClick={handleTryAnotherDisableMethod}>Try another method</button>}
              </div>
            )}
            {twoFAError && !disablingTwoFA && <p className="text-danger mt-3 mb-0" role="alert" style={{ fontSize: "13px" }}>{twoFAError}</p>}
          </section>
        </div>
      )}
    </div>
  );
};

export default AccountSecurity;
